import {useEffect, useState} from "react";
import {useParams} from "react-router-dom";

const PRODUCT_LABELS = {
  "bundle.premium": "Disney+ Premium",
  "bundle.standard": "Disney+ Estándar",
  "bundle.swads": "Disney+ Estándar con anuncios",
};

const productLabel = (product) => PRODUCT_LABELS[product] || product || "Disney+";

export default function DisneyRenewal() {
  const {token} = useParams();

  const [step, setStep] = useState("loading"); // loading | blocked | email | otp | choose | done
  const [tokenInfo, setTokenInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(false);

  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [resultMessage, setResultMessage] = useState(null);
  const [activationUrl, setActivationUrl] = useState(null);

  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']") || Object.assign(document.createElement('link'), {rel: 'icon'});
    link.href = '/disney/favicon.ico';
    document.head.appendChild(link);
    document.title = 'Renueva tu cuenta de Disney+';
  }, []);

  useEffect(() => {
    if (token) fetchTokenInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const api = async (path, body) => {
    const res = await fetch(`/api/v1/disney-renewal/${token}${path}`, body ? {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({data: body}),
    } : undefined);
    const data = await res.json();
    if (!res.ok || data.result === 'error') throw new Error(data.error || data.message || `Error ${res.status}`);
    return data;
  };

  const fetchTokenInfo = async () => {
    setLoading(true);
    try {
      const data = await api('');
      setTokenInfo(data);
      if (data.status === 'blocked-country') return setStep('blocked');
      if (data.status !== 'available') return setStep('invalid');
      setStep('email');
    } catch (err) {
      setErrorMessage('No fué posible cargar este voucher.');
      setStep('invalid');
    } finally {
      setLoading(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);
    try {
      await api('/request-otp', {email});
      setStep('otp');
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);
    try {
      const data = await api('/verify-otp', {otp});
      setAccounts(data.accounts || []);
      setStep('choose');
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renewAccount = async (disneyTokenId) => {
    setErrorMessage(null);
    setLoading(true);
    try {
      const data = await api('/renew', {disneyTokenId});
      setResultMessage(data.message || 'Tu cuenta fue renovada exitosamente.');
      setStep('done');
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createNew = async (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);
    try {
      const data = await api('/new', {customerName});
      setActivationUrl(data.activationUrl);
      setResultMessage('Tu cuenta nueva fue creada exitosamente.');
      setStep('done');
    } catch (err) {
      setErrorMessage(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-4 py-8"
      style={{
        backgroundImage: 'url(/disney/fondo.jpg.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 sm:p-8">
        <img src="/disney/logo.webp" alt="Disney+" className="h-10 mx-auto mb-6" />

        {step === 'loading' && (
          <p className="text-center text-gray-700 font-medium">Cargando...</p>
        )}

        {step === 'blocked' && (
          <p className="text-center text-gray-800 font-bold">
            Esta promoción solo está disponible para usuarios ubicados en Colombia.
          </p>
        )}

        {step === 'invalid' && (
          <p className="text-center text-gray-800 font-bold">
            {tokenInfo?.status === 'used'
              ? 'Este voucher ya fue utilizado.'
              : tokenInfo?.status === 'expired'
                ? 'Este voucher ha expirado.'
                : 'Este enlace no es válido. Verifica que sea correcto.'}
          </p>
        )}

        {step === 'email' && (
          <form onSubmit={submitEmail} className="flex flex-col gap-4">
            <p className="text-gray-700 text-sm">
              Ingresa tu correo para ver si tienes una cuenta {productLabel(tokenInfo?.product)} activa para renovar,
              o crear una cuenta nueva.
            </p>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@email.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg"
            />
            {errorMessage && <p className="text-red-600 text-sm">{errorMessage}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#113CCF] hover:bg-[#0d2fa3] disabled:opacity-50 text-white font-bold rounded-lg"
            >
              {loading ? 'Enviando...' : 'Continuar'}
            </button>
          </form>
        )}

        {step === 'otp' && (
          <form onSubmit={submitOtp} className="flex flex-col gap-4">
            <p className="text-gray-700 text-sm">
              Te enviamos un código de verificación a <strong>{email}</strong>. Ingrésalo para continuar.
            </p>
            <input
              type="text"
              required
              inputMode="numeric"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-center tracking-[0.5em] text-xl"
            />
            {errorMessage && <p className="text-red-600 text-sm">{errorMessage}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-[#113CCF] hover:bg-[#0d2fa3] disabled:opacity-50 text-white font-bold rounded-lg"
            >
              {loading ? 'Verificando...' : 'Verificar código'}
            </button>
          </form>
        )}

        {step === 'choose' && (
          <div className="flex flex-col gap-4">
            {accounts.length > 0 && (
              <div className="flex flex-col gap-2">
                <p className="text-gray-700 text-sm font-medium">Cuentas disponibles para renovar:</p>
                {accounts.map(acc => (
                  <div key={acc.id} className="border border-gray-200 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-gray-800 text-sm font-medium">{productLabel(acc.product)}</span>
                    <button
                      onClick={() => renewAccount(acc.id)}
                      disabled={loading}
                      className="px-3 py-1.5 bg-[#113CCF] hover:bg-[#0d2fa3] disabled:opacity-50 text-white text-sm font-bold rounded-lg"
                    >
                      Renovar
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="border-t border-gray-200 pt-4">
              <p className="text-gray-700 text-sm font-medium mb-2">
                {accounts.length > 0 ? 'O crea una cuenta nueva:' : 'No tienes cuentas activas para renovar. Crea una cuenta nueva:'}
              </p>
              <form onSubmit={createNew} className="flex flex-col gap-3">
                <input
                  type="text"
                  required
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Tu nombre completo"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
                {errorMessage && <p className="text-red-600 text-sm">{errorMessage}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-2.5 bg-gray-800 hover:bg-black disabled:opacity-50 text-white font-bold rounded-lg"
                >
                  {loading ? 'Creando...' : 'Crear cuenta nueva'}
                </button>
              </form>
            </div>
          </div>
        )}

        {step === 'done' && (
          <div className="flex flex-col gap-4 text-center">
            <p className="text-gray-800 font-bold">{resultMessage}</p>
            {activationUrl && (
              <a
                href={activationUrl}
                className="block w-full py-2.5 bg-[#113CCF] hover:bg-[#0d2fa3] text-white font-bold rounded-lg text-center"
              >
                Continuar a activar
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
